/**
 * PocketFiler - Product Tour
 * Lightweight first-time user tour. No external deps (works with React 19).
 * Shows a series of tooltip-style cards pointing to key UI elements.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from '../i18n';
import api from '../lib/api';
import { X, ArrowRight, ArrowLeft, Sparkles, Check } from 'lucide-react';


const TOUR_STEPS = [
  {
    key: 'welcome',
    title: 'Welcome aboard! 🎉',
    body: 'Let\'s take a quick 60-second tour. This will show you where everything lives so you can hit the ground running.',
    position: 'center',
  },
  {
    key: 'sidebar',
    title: 'Your navigation',
    body: 'All your tools live in the sidebar. Dashboard, Deals, Documents, Contracts, Meetings — everything is one click away.',
    selector: '[data-tour="sidebar"]',
    position: 'right',
  },
  {
    key: 'dashboard',
    title: 'Your command center',
    body: 'The dashboard shows what needs your attention — deals in progress, upcoming meetings, pending tasks, and AI briefings.',
    selector: '[data-tour="dashboard"]',
    position: 'bottom',
  },
  {
    key: 'create',
    title: 'Create anything, fast',
    body: 'AI-powered contracts, deal rooms, client onboarding — all start with one click. No training needed.',
    selector: '[data-tour="create-button"]',
    position: 'bottom',
  },
  {
    key: 'feedback',
    title: 'We\'re building this with you',
    body: 'See that chat button in the corner? Hit it anytime to report bugs, request features, or share feedback. We read every message.',
    selector: '[data-tour="feedback-button"]',
    position: 'top',
  },
  {
    key: 'done',
    title: 'You\'re all set!',
    body: 'Questions? Feedback? Stuck? Use the feedback button or check Settings → Help. Now go make something great.',
    position: 'center',
  },
];


export default function ProductTour() {
  const { t } = useTranslation();
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [position, setPosition] = useState(null);

  useEffect(() => {
    // Check if user has seen the tour
    checkTourStatus();
  }, []);

  const checkTourStatus = async () => {
    try {
      const status = await api.get('/onboarding/status');
      if (status.product_tour_seen) return;
      // Only show tour if onboarding role is picked (they've signed up + done welcome)
      if (status.use_case) {
        // Delay 1.5s to let the page settle
        setTimeout(() => setActive(true), 1500);
      }
    } catch (e) {
      // ignore — don't block the app
    }
  };

  useEffect(() => {
    if (!active) return;
    const step = TOUR_STEPS[stepIndex];
    if (step.position === 'center' || !step.selector) {
      setPosition(null);
      return;
    }

    const el = document.querySelector(step.selector);
    if (!el) {
      setPosition(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    setPosition({
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
      tooltipPos: step.position,
    });
  }, [stepIndex, active]);

  const handleNext = () => {
    if (stepIndex < TOUR_STEPS.length - 1) {
      setStepIndex(stepIndex + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (stepIndex > 0) setStepIndex(stepIndex - 1);
  };

  const handleComplete = async () => {
    setActive(false);
    try {
      await api.post('/onboarding/event', { event: 'tour_completed' });
    } catch (e) {}
  };

  const handleSkip = async () => {
    setActive(false);
    try {
      await api.post('/onboarding/event', { event: 'tour_dismissed' });
    } catch (e) {}
  };

  if (!active) return null;

  const step = TOUR_STEPS[stepIndex];
  const isLast = stepIndex === TOUR_STEPS.length - 1;
  const isCenter = step.position === 'center' || !position;

  // Calculate tooltip position
  let tooltipStyle = {};
  if (!isCenter && position) {
    const PAD = 12;
    switch (position.tooltipPos) {
      case 'right':
        tooltipStyle = { top: position.top, left: position.left + position.width + PAD };
        break;
      case 'left':
        tooltipStyle = { top: position.top, right: window.innerWidth - position.left + PAD };
        break;
      case 'bottom':
        tooltipStyle = { top: position.top + position.height + PAD, left: Math.max(16, position.left) };
        break;
      case 'top':
      default:
        tooltipStyle = { bottom: window.innerHeight - position.top + PAD, left: Math.max(16, position.left) };
    }
  }

  return (
    <>
      {/* Dark overlay */}
      <div
        className="fixed inset-0 z-[60] bg-black/60 pointer-events-auto"
        style={{ backdropFilter: 'blur(1px)' }}
      />

      {/* Spotlight cutout (if targeting an element) */}
      {!isCenter && position && (
        <div
          className="fixed z-[61] pointer-events-none rounded-lg ring-4 ring-primary ring-offset-2 ring-offset-transparent"
          style={{
            top: position.top - 4,
            left: position.left - 4,
            width: position.width + 8,
            height: position.height + 8,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)',
            transition: 'all 0.3s ease',
          }}
        />
      )}

      {/* Tour card */}
      <div
        className={`fixed z-[62] bg-background rounded-2xl shadow-2xl border p-5 max-w-sm w-[min(90vw,24rem)] ${
          isCenter ? 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2' : ''
        }`}
        style={isCenter ? {} : tooltipStyle}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <Sparkles className="h-4 w-4" />
            </div>
            <span className="text-xs font-medium text-muted-foreground">
              {stepIndex + 1} / {TOUR_STEPS.length}
            </span>
          </div>
          <button
            onClick={handleSkip}
            className="h-7 w-7 rounded-md hover:bg-muted flex items-center justify-center text-muted-foreground"
            title="Skip tour"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <h3 className="text-lg font-bold mb-1.5">{step.title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed mb-5">{step.body}</p>

        {/* Progress dots */}
        <div className="flex items-center gap-1 mb-4">
          {TOUR_STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1 rounded-full transition-all ${
                i === stepIndex ? 'bg-primary w-6' : i < stepIndex ? 'bg-primary/50 w-1.5' : 'bg-muted w-1.5'
              }`}
            />
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between gap-2">
          {stepIndex > 0 ? (
            <button onClick={handlePrev} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
              <ArrowLeft className="h-3 w-3" /> Back
            </button>
          ) : (
            <button onClick={handleSkip} className="text-xs text-muted-foreground hover:text-foreground">
              Skip tour
            </button>
          )}
          <button
            onClick={handleNext}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
          >
            {isLast ? (
              <><Check className="h-3.5 w-3.5" /> Got it</>
            ) : (
              <>Next <ArrowRight className="h-3.5 w-3.5" /></>
            )}
          </button>
        </div>
      </div>
    </>
  );
}
